import AppAlert from '@/interfaces/common/AppAlert'
import LedgerTransactionTypeDao from '@/interfaces/common/LedgerTransactionType.dao'
import PaymentTransactionTypeDao from '@/interfaces/common/PaymentTransactionType.dao'
import { PaymentMethodDao } from '@/interfaces/order/PaymentMethod.dao'
import CountryDao from '@/interfaces/portfolio/Country.dao'
import CurrencyDao from '@/interfaces/portfolio/Currency.dao'
import { SystemEventStatusesDto } from '@/interfaces/system-events/SystemEventStatuses.dto'
import { ApiPluginInterface } from '@/plugins/api-plugin'
import { defineStore } from 'pinia'
import MarketplacesDao from '@/interfaces/common/Marketplaces.dao'
import AmazonAccountsDao from '@/interfaces/common/AmazonAccounts.dao'
import AmazonRegionsDao from '@/interfaces/common/AmazonRegions.dao'
import { MarketplaceFeedRequestTypesDao } from '@/interfaces/system-log/MarketplaceFeedRequestTypes.dao'
import ChannelProfilesDao from '@/interfaces/common/ChannelProfiles.dao'
import ClientsDao from '@/interfaces/client/Clients.dao'
import { IRequestParameters } from '@/plugins/api-plugin/services/api.service'
import SellersDao from '@/interfaces/common/Sellers.dao'
import TaxClassesDao from '@/interfaces/common/TaxClasses.dao'
import ChannelAccountDao from '@/interfaces/order/ChannelAccount.dao'
import SalesOrganisationsDao from '@/interfaces/common/SalesOrganisations.dao'
export const useCommonStore = defineStore({
    id: 'common',
    state: () => ({
        alerts: [] as AppAlert[],
        countries: [] as CountryDao[],
        currencies: [] as CurrencyDao[],
        clients: [] as ClientsDao[],
        paymentMethods: [] as PaymentMethodDao[],
        ledgerTransactionTypes: [] as LedgerTransactionTypeDao[],
        systemEventStatuses: [] as SystemEventStatusesDto[],
        paymentTransactionTypes: [] as PaymentTransactionTypeDao[],
        marketplaces: [] as MarketplacesDao[],
        amazonAccounts: [] as AmazonAccountsDao[],
        amazonRegions: [] as AmazonRegionsDao[],
        marketplaceFeedRequestTypes: [] as MarketplaceFeedRequestTypesDao[],
        channelProfiles: [] as ChannelProfilesDao[],
        channelAccounts: [] as ChannelAccountDao[],
        configurationsCategories: [] as string[],
        sellers: [] as SellersDao[],
        sellerMap: new Map<string, SellersDao>(),
        taxClasses: [] as TaxClassesDao[],
        salesOrganisations: [] as SalesOrganisationsDao[],
    }),
    actions: {
        pushAlert(payload: AppAlert) {
            this.alerts.push({
                ...payload,
                visible: true,
                id: new Date().getTime() + this.alerts.length,
            })
        },
        pushSuccess(alertTitle: string, alertMessage: string) {
            this.pushAlert({ alertTitle, alertMessage, type: 'green-lighten-1', visible: true, timeout: 3000 })
        },
        pushError(alertTitle: string, alertMessage: string) {
            this.pushAlert({ alertTitle, alertMessage, type: 'red-lighten-2', visible: true, timeout: 10000 })
        },
        async loadCountries(api?: ApiPluginInterface, reload = false) {
            if (!reload && this.countries.length) return
            const response = await api?.common.getCountries()
            this.countries = response?.[0] || []
        },
        async loadCurrencies(api?: ApiPluginInterface, reload = false) {
            if (!reload && this.currencies.length) return
            const response = await api?.common.getCurrencies()
            this.currencies = response?.[0] || []
        },
        async loadPaymentMethods(api?: ApiPluginInterface, reload = false) {
            if (!reload && this.paymentMethods.length) return
            const response = await api?.common.getPaymentMethods()
            this.paymentMethods = response?.[0] || []
        },
        async loadLedgerTransactionTypes(api?: ApiPluginInterface, reload = false) {
            if (!reload && this.ledgerTransactionTypes.length) return
            const response = await api?.common.getLedgerTransactionTypes()
            this.ledgerTransactionTypes = response?.[0] || []
        },
        async loadSystemEventStatuses(api?: ApiPluginInterface, reload = false) {
            if (!reload && this.systemEventStatuses.length) return
            const response = await api?.systemEvents.getSystemEventStatuses({ limit: 1000 })
            this.systemEventStatuses = response?.[0] || []
        },
        async loadPaymentTransactionTypes(api?: ApiPluginInterface, reload = false) {
            if (!reload && this.paymentTransactionTypes.length) return
            const response = await api?.common.getPaymentTransactionTypes()
            this.paymentTransactionTypes = response?.[0] || []
        },
        async loadMarketplaces(api?: ApiPluginInterface, reload = false) {
            if (!reload && this.marketplaces.length) return
            const response = await api?.common.getMarketplaces()
            this.marketplaces = response?.[0] || []
        },
        async loadAmazonAccounts(api?: ApiPluginInterface, reload = false) {
            if (!reload && this.amazonAccounts.length) return
            const response = await api?.common.getAmazonAccounts()
            this.amazonAccounts = response?.[0] || []
        },
        async loadAmazonRegions(api?: ApiPluginInterface, reload = false) {
            if (!reload && this.amazonRegions.length) return
            const response = await api?.common.getAmazonRegions({ limit: 1000 })
            this.amazonRegions = response?.[0] || []
        },
        async loadMarketplaceFeedRequestTypes(api?: ApiPluginInterface, reload = false) {
            if (!reload && this.marketplaceFeedRequestTypes.length) return
            const response = await api?.systemLogs.getMarketplaceFeedRequestTypes({ limit: 1000 })
            this.marketplaceFeedRequestTypes = response?.[0] || []
        },
        async loadChannelProfiles(api?: ApiPluginInterface, reload = false) {
            if (!reload && this.channelProfiles.length) return
            const response = await api?.common.getChannelProfiles({ limit: 1000 })
            this.channelProfiles = response?.[0] || []
        },
        async loadChannelAccounts(api?: ApiPluginInterface, reload = false) {
            if (!reload && this.channelAccounts.length) return
            const response = await api?.common.getChannelAccounts({ limit: 1000 })
            this.channelAccounts = response?.[0] || []
        },
        async loadClients(api?: ApiPluginInterface, reload = false) {
            if (!reload && this.clients.length) return
            const response = await api?.client.getClients({ limit: 1000, sortBy: 'Name:asc' })
            this.clients = response?.[0] || []
        },
        async loadConfigurationsCategories(params: IRequestParameters, api?: ApiPluginInterface, reload = false) {
            if (!reload && this.configurationsCategories.length) return
            const response = await api?.common.getConfigurationsCategories(params)
            this.configurationsCategories = response || []
        },
        async loadSellers(api?: ApiPluginInterface, reload = false) {
            if (!reload && this.sellers.length) return
            const response = await api?.portfolio.getSellersCompact({ limit: 20000 })
            const sellers = response?.[0] || []
            this.sellerMap.clear()
            this.sellers = sellers.map((sellerArray: (string | number)[]) => {
                const seller = {
                    Id: sellerArray[0] as number,
                    Name: sellerArray[1] as string,
                    NameSellerId: `${sellerArray[1]} (${sellerArray[2]})`,
                    SellerId: sellerArray[2] as string,
                    SellerType: sellerArray[3] as string,
                }
                this.sellerMap.set(seller.SellerId, seller)
                return seller
            })
        },
        async loadTaxClasses(api?: ApiPluginInterface, reload = false) {
            if (!reload && this.taxClasses.length) return
            const response = await api?.common.getTaxClasses({ limit: 1000 })
            this.taxClasses = response?.[0] || []
        },
        async loadSalesOrganisations(api?: ApiPluginInterface, reload = false) {
            if (!reload && this.salesOrganisations.length) return
            const response = await api?.common.getSalesOrganisations({ limit: 1000 })
            this.salesOrganisations = response?.[0] || []
        },
    },
})
